/* @flow */
import apiRequest from 'lib/apiRequest';
import Entity from 'lib/Entity';
import { GLYPHS } from 'constants/MediumConstants';
import jstz from 'jstimezonedetect';

export type CreateOutboundDeviceOptions = {
  skipUpdateDevicePreferences?: boolean,
};

export type ConfirmableDeviceType = 'phoneNumber' | 'email';

export default class Device extends Entity {
  isEnabled() {
    return this.get('enabled');
  }

  isVerified(): boolean {
    return !!this.get('verified');
  }

  isDeliverable(): boolean {
    return !!this.get('deliverable');
  }

  setEnabled(value: boolean) {
    return !!this.set('enabled', value);
  }

  getLabel(): string {
    return this.get('label') || this.getDefaultLabel();
  }

  getEndpoint() {
    return this.get('endpoint');
  }

  getDefaultLabel(): string {
    switch (this.getType()) {
      case 'email':
        return 'Email device';
      case 'apns':
        return 'Apple device';
      case 'sms':
        return 'Sms device';
      case 'gcm':
        return 'Android device';
      case 'adm':
        return 'Amazon device';
      default:
        return 'Device';
    }
  }

  getType() {
    return this.get('type');
  }

  getNotificationType(): string {
    switch (this.getType()) {
      case 'email':
        return 'email notifications';
      case 'gcm':
        return 'push notifications';
      case 'apns':
        return 'push notifications';
      case 'sms':
        return 'text notifications';
      default:
        return 'notifications';
    }
  }

  getMessageType(): string {
    switch (this.getType()) {
      case 'email':
        return 'emails';
      case 'gcm':
        return 'push notifications';
      case 'apns':
        return 'push notifications';
      case 'sms':
        return 'text messages';
      default:
        return 'notifications';
    }
  }

  getGlyphName() {
    return GLYPHS[this.getType()];
  }

  cannotBeEnabled(): boolean {
    return this.getType() === 'email' && !this.isDeliverable();
  }

  enable(): Promise<void> {
    // $FlowFixMe
    return apiRequest(`/devices/${this.id}/enable`, 'POST').then(device => this.set(device));
  }

  disable(): Promise<void> {
    // $FlowFixMe
    return apiRequest(`/devices/${this.id}/disable`, 'POST').then(device => this.set(device));
  }

  delete(): Promise<void> {
    return apiRequest(`/devices/${this.id}`, 'DELETE');
  }

  resendConfirmationEmail(): Promise<void> {
    return apiRequest('/devices/email/resend', 'POST', { uid: this.getEndpoint() });
  }

  static confirmEmailDevice(requestBody: {
    verification_token?: ?string,
    verification_code?: ?string,
    address?: ?string,
  }): Promise<void> {
    return apiRequest('/devices/email/confirm', 'POST', requestBody);
  }

  static enableEmailDevice(): Promise<Device> {
    return apiRequest('/devices/email/enable', 'POST').then(
      device => ((Device.fromJSON(device): any): Device)
    );
  }

  static createEmailDevice(uid: string): Promise<Device> {
    return apiRequest('/devices/email', 'POST', {
      uid,
    }).then(device => ((Device.fromJSON(device): any): Device));
  }

  /* you give this method 'sms://+4156152427' or 'email://q@q.com' and the
   * promise will be resolved if the user owning +4156152427 can
   * sign into the web (has a password and email), rejected otherwise.
   *
   * 200 Success. this endpoint belongs to a full account user
   * 403 The address is invalid
   * 404 The address is uknown or belong to a not full account user.
   */
  static belongsToFullAccount(address: string) {
    return apiRequest('/devices/user', 'GET', {
      address,
    });
  }

  /* Initiate an outbound verification for a device:
   *
   * 201 Success. An address and a code have been generated for further verification.
   * 403 The address is specified properly but can not be verified.
   * 409 The currently authenticated user already has the device.
   * 422 The syntax of the address is invalid.
   */
  static outboundVerify(address: string, receiveCodeOverVoice: boolean = false) {
    return apiRequest('/devices/outbound_verification/code', 'POST', {
      address,
      receive_code_over_voice: receiveCodeOverVoice,
    });
  }

  /* Create the device after having initiated a outbound verification
   * Pass the address returned by outboundVerify and the code
   * provided by the user (received by sms or email).
   *
   * 201  Success. The device has been added.
   * 403  The provided token and code are invalid.
   */
  static createOutboundDevice(
    address: string,
    code: string,
    options: CreateOutboundDeviceOptions = {}
  ) {
    const params: any = { address, code };

    if (options.skipUpdateDevicePreferences) {
      params['skip_update_device_preferences'] = true;
    }

    return apiRequest('/devices/outbound_verification', 'POST', params);
  }

  /* Initiate the merge request for merging two email devices

   * 201 Success. A token has been generated for further verification.
   * 404 The request could not be found.
   * 403 The address is specified properly but can not be verified.
   * 409 The currently authenticated user already has the device.
   * 422 The syntax of the address is invalid.
   */
  static createEmailMergeRequest(endpoint: string): Promise<void> {
    return apiRequest('/devices/email/merge_confirmation', 'POST', {
      endpoint,
    });
  }

  /* Merge two two email accounts after having initiated a merge request
   * Pass the token returned by createEmailRequest and the token returned
   * by the User. Also pass the ID of the two users.
  */
  static emailMergeRequest(code: string, oldUserEmail: string): Promise<void> {
    const tz = jstz.determine();
    const timezone = tz.name();
    return apiRequest('/devices/email/merge_emails', 'POST', {
      code,
      email: oldUserEmail,
      timezone,
    });
  }
}
